import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { PlayCircle, Users, Trophy, Star, ChevronRight } from 'lucide-react';

const Landing = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const games = [
    {
      title: "Conocimiento de Orishas",
      description: "Aprende sobre plantas, animales, colores y días de cada Orisha",
      icon: "🌿",
      difficulty: "Principiante",
      players: "1,234"
    },
    {
      title: "Signos de Ifá",
      description: "Domina los 256 signos sagrados y sus significados",
      icon: "🔮",
      difficulty: "Intermedio",
      players: "856"
    },
    {
      title: "Sureye y Patakíes",
      description: "Comprende los sureyes y historias de cada signo",
      icon: "📚",
      difficulty: "Avanzado",
      players: "542"
    },
    {
      title: "Rituales Sagrados",
      description: "Aprende moyubas, ceremonias y rituales importantes",
      icon: "🕯️",
      difficulty: "Experto",
      players: "321"
    },
    {
      title: "Idioma Yoruba",
      description: "Domina el idioma sagrado con método interactivo",
      icon: "🗣️",
      difficulty: "Todos los niveles",
      players: "2,187"
    }
  ];

  const stats = [
    { number: "12,000+", label: "Estudiantes Graduados" },
    { number: "85+", label: "Países Representados" },
    { number: "4.8/5", label: "Calificación Promedio" },
    { number: "95%", label: "Tasa de Éxito" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section relative">
        <div className="container mx-auto px-6 py-20 relative z-10">
          <div className="text-center text-white">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_955bee34-da15-4f79-944e-efcba3acaf46/artifacts/6o2v55w8_CaminosdeIfa_Linkedin.jpg"
                alt="Caminos de Ifá"
                className="w-32 h-32 mx-auto mb-4 rounded-full shadow-2xl border-4 border-yellow-400"
              />
              <h1 className="font-cinzel text-5xl md:text-7xl font-bold mb-4">
                Caminos de <span className="text-gradient-gold">Ifá</span>
              </h1>
            </div>
            
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-90 leading-relaxed">
              Descubre los misterios ancestrales de la religión Yoruba a través de juegos interactivos y 
              experiencias de aprendizaje diseñadas para todas las edades
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <Link 
                to={isAuthenticated ? "/dashboard" : "/register"}
                className="btn-gold px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:scale-105 transform transition-all duration-300 flex items-center gap-2"
              >
                <PlayCircle className="w-6 h-6" />
                {isAuthenticated ? "Ir al Dashboard" : "Comenzar Aprendizaje"}
              </Link>
              <Link 
                to="/login"
                className="btn-primary px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:scale-105 transform transition-all duration-300 flex items-center gap-2"
              >
                Iniciar Sesión
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="glass rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-yellow-300 mb-2">{stat.number}</div>
                  <div className="text-sm opacity-80">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 floating-element opacity-30">
          <div className="w-20 h-20 rounded-full bg-yellow-400 blur-xl"></div>
        </div>
        <div className="absolute bottom-32 right-16 floating-element opacity-20">
          <div className="w-32 h-32 rounded-full bg-green-400 blur-xl"></div>
        </div>
        <div className="absolute top-1/2 left-1/4 floating-element opacity-25">
          <div className="w-16 h-16 rounded-full bg-yellow-300 blur-lg"></div>
        </div>
      </section>

      {/* Games Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-yellow-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-emerald-800 mb-6">
              Juegos de Aprendizaje
            </h2>
            <p className="text-xl text-emerald-700 max-w-3xl mx-auto">
              Explora la sabiduría ancestral a través de experiencias interactivas diseñadas 
              para profundizar tu comprensión de la tradición Ifá
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {games.map((game, index) => (
              <div key={index} className="game-card bg-white rounded-2xl p-8 shadow-lg relative overflow-hidden group">
                <div className="text-6xl mb-6 text-center">{game.icon}</div>
                <h3 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">{game.title}</h3>
                <p className="text-emerald-600 mb-6 leading-relaxed">{game.description}</p>
                
                <div className="flex justify-between items-center mb-6">
                  <span className="level-badge">
                    <Star className="w-4 h-4" />
                    {game.difficulty}
                  </span>
                  <span className="text-emerald-600 text-sm flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {game.players} jugadores
                  </span>
                </div>
                
                <Link
                  to={isAuthenticated ? "/dashboard" : "/register"}
                  className="w-full btn-primary text-center py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-xl"
                >
                  {isAuthenticated ? "Jugar Ahora" : "Jugar Ahora"}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-emerald-800 mb-6">
              ¿Por qué elegir Caminos de Ifá?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-6">
                <Trophy className="w-8 h-8 text-emerald-800" />
              </div>
              <h3 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">Sistema de Niveles</h3>
              <p className="text-emerald-600 leading-relaxed">
                Avanza através de diferentes niveles de dificultad adaptados a tu conocimiento actual
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-emerald-800" />
              </div>
              <h3 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">Comunidad Global</h3>
              <p className="text-emerald-600 leading-relaxed">
                Únete a una comunidad mundial de estudiantes y practicantes de la tradición Yoruba
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-6">
                <Star className="w-8 h-8 text-emerald-800" />
              </div>
              <h3 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">Contenido Auténtico</h3>
              <p className="text-emerald-600 leading-relaxed">
                Aprende de fuentes tradicionales y maestros experimentados en la religión Ifá
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-primary">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-white mb-6">
            Comienza tu Viaje Espiritual Hoy
          </h2>
          <p className="text-xl text-white opacity-90 mb-8 max-w-3xl mx-auto">
            Únete a miles de estudiantes que ya están explorando los caminos sagrados de Ifá
          </p>
          <Link 
            to={isAuthenticated ? "/dashboard" : "/register"}
            className="btn-gold px-12 py-4 text-xl font-bold rounded-xl shadow-lg hover:scale-105 transform transition-all duration-300 inline-flex items-center gap-3"
          >
            <PlayCircle className="w-6 h-6" />
            {isAuthenticated ? "Ir al Dashboard" : "Crear Cuenta Gratuita"}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-900 text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_955bee34-da15-4f79-944e-efcba3acaf46/artifacts/6o2v55w8_CaminosdeIfa_Linkedin.jpg"
            alt="Caminos de Ifá"
            className="w-16 h-16 mx-auto mb-4 rounded-full"
          />
          <h3 className="font-cinzel text-2xl font-bold mb-4">Caminos de Ifá</h3>
          <p className="text-emerald-300 mb-6 max-w-2xl mx-auto">
            Preservando y compartiendo la sabiduría ancestral Yoruba para las nuevas generaciones
          </p>
          <div className="text-emerald-400 text-sm">
            © 2020 Caminos de Ifá. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;